import React, { useState, useEffect, useMemo } from "react";
import "./BackgroundImageControlsMenu.css";
import BackgroundImageCatagoryMenu from "./BackgroundImageCatagoryMenu";
import { getBackgroundPresets } from "../../../../services/api/catalog/background_presets";
import { mapImageCatalogToSections } from "./backgroundPresetsMapper";
import { IS_APP_V1 } from "../../../../config/app/appVersion";
import ImagePickerModal from "../../../../components/popups/imagePicker/ImagePickerModal";
import { useImagePickerModal } from "../../../../hooks/app/useImagePickerModal";
import { useAuth } from "@clerk/clerk-react";
import {
  libraryItemCreationId,
  libraryItemExportId,
} from "../../../../utils/openLibraryInEditor";
import {
  fetchUploadImageBlob,
  fetchCreationImageBlob,
  fetchExportImageBlob,
} from "../../../../services/api/editor/background_removal";
import { getUploadSizeError } from "../../../../utils/uploadLimits";

const BackgroundImageControlsMenu = ({
  onApplyImageBackground = () => {},
  onViewingCategoryChange = () => {},
  onUnsplashSearch,
  onUnsplashTrackDownload,
}) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [viewingCategory, setViewingCategory] = useState(false);
  const [categoryData, setCategoryData] = useState({ name: "", images: [] });
  const [stockQuery, setStockQuery] = useState("");
  const [stockSearching, setStockSearching] = useState(false);
  const [stockResults, setStockResults] = useState([]);
  const [stockSearchError, setStockSearchError] = useState(null);
  const [sections, setSections] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(null);
  const [applyBusy, setApplyBusy] = useState(false);
  const [attribution, setAttribution] = useState(null);
  const { getToken } = useAuth();
  const { openImagePicker, imagePickerProps } = useImagePickerModal();
  const showLocalUpload = !IS_APP_V1;
  // Image tab never uses old S3 Texture/Colors samples — curated stock only.
  const showStockSearch = !IS_APP_V1;
  const catalogSource = "unsplash";

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(null);
    getBackgroundPresets({ catalog: "image", source: catalogSource })
      .then((res) => {
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) {
          setSections(mapImageCatalogToSections(res.data));
        } else {
          setSections(null);
          setCatalogError("Could not load backgrounds");
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSections(null);
          setCatalogError(err?.message || "Could not load backgrounds");
        }
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogSource]);

  const applyUrl = (item) => item?.full_url || item?.image || item?.image_url;

  const rememberAttribution = (item) => {
    if (!item?.photographer && !item?.attribution) {
      setAttribution(null);
      return;
    }
    setAttribution({
      photographer: item.photographer,
      photographer_url: item.photographer_url,
      photo_url: item.photo_url,
      text: item.attribution,
    });
  };

  const applyWithTracking = async (item) => {
    const url = applyUrl(item);
    if (!url) return;

    const credit =
      item?.photographer || item?.attribution
        ? {
            photographer: item.photographer || null,
            photographer_url: item.photographer_url || null,
            photo_url: item.photo_url || null,
          }
        : null;

    // Always apply from catalog/search URLs first (images.unsplash.com CDN — no API quota).
    // Unsplash guidelines still want a download trigger on select; do that best-effort after.
    rememberAttribution(item);
    onApplyImageBackground(url, credit);

    if (!item?.external_asset_id && !item?.download_location) {
      return;
    }
    if (!onUnsplashTrackDownload) {
      return;
    }

    setApplyBusy(true);
    try {
      const body = item.external_asset_id
        ? { external_asset_id: item.external_asset_id }
        : {
            download_location: item.download_location,
            provider_asset_id: item.id || item.provider_asset_id,
            image_url: url,
          };
      const tracked = await onUnsplashTrackDownload(body);
      if (tracked?.photographer || tracked?.photographer_url || tracked?.photo_url) {
        const nextCredit = {
          photographer: tracked?.photographer || item.photographer,
          photographer_url: tracked?.photographer_url || item.photographer_url,
          photo_url: tracked?.photo_url || item.photo_url,
        };
        rememberAttribution({ ...item, ...nextCredit });
        onApplyImageBackground(url, nextCredit);
      }
    } catch (err) {
      // Do not block canvas apply — track can fail on rate limit / auth.
      console.warn("[stock] track-download failed after apply:", err?.message || err);
    } finally {
      setApplyBusy(false);
    }
  };

  const applyDataUrlFromFile = (file) => {
    if (!file?.type?.startsWith("image/")) return;
    if (getUploadSizeError(file)) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setAttribution(null);
      onApplyImageBackground(event.target.result, null);
    };
    reader.readAsDataURL(file);
  };

  const openBackgroundPicker = () => {
    openImagePicker({
      onPickFile: applyDataUrlFromFile,
      onPickLibrary: async (item) => {
        try {
          const token = await getToken();
          if (!token) return;
          let blob;
          if (item.kind === "upload") {
            blob = await fetchUploadImageBlob(token, libraryItemCreationId(item));
          } else if (item.kind === "export") {
            blob = await fetchExportImageBlob(token, libraryItemExportId(item));
          } else {
            blob = await fetchCreationImageBlob(token, libraryItemCreationId(item));
          }
          const url = URL.createObjectURL(blob);
          setAttribution(null);
          onApplyImageBackground(url, null);
        } catch (err) {
          console.error(err);
        }
      },
    });
  };

  const handleTextureClick = (textureId, item) => {
    setSelectedImages([...selectedImages, textureId]);
    applyWithTracking(item);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "#2a2a2a";
  };

  const handleDragLeave = (e) => {
    e.currentTarget.style.backgroundColor = "transparent";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "transparent";
    openBackgroundPicker();
  };

  const handleBackToMain = () => {
    setViewingCategory(false);
    setCategoryData({ name: "", images: [] });
    onViewingCategoryChange(false);
  };

  const handleStockSearch = async () => {
    if (!onUnsplashSearch || !stockQuery.trim()) return;
    setStockSearching(true);
    setStockSearchError(null);
    setStockResults([]);
    try {
      const data = await onUnsplashSearch(stockQuery.trim());
      const imgs = Array.isArray(data?.images) ? data.images : [];
      setStockResults(imgs.slice(0, 30));
    } catch (err) {
      setStockSearchError(err?.message || "Search failed");
    } finally {
      setStockSearching(false);
    }
  };

  const clearSearch = () => {
    setStockQuery("");
    setStockResults([]);
    setStockSearchError(null);
  };

  const handlePickStockImage = (item) => {
    applyWithTracking({
      ...item,
      source: "unsplash",
      full_url: item.image_url || item.full_url,
      image: item.thumbnail_url || item.image_url,
    });
  };

  const sectionList = useMemo(() => sections || [], [sections]);
  const showingSearchResults = stockResults.length > 0;

  const PREVIEW_COUNT = 5;
  const SEE_ALL_MAX = 25;

  const handleSeeAllClick = (categoryName, categoryImages) => {
    setCategoryData({
      name: categoryName,
      images: (categoryImages || []).slice(0, SEE_ALL_MAX),
    });
    setViewingCategory(true);
    onViewingCategoryChange(true);
  };

  const renderCategorySection = (sectionTitle, groups) => (
    <div className="category-section" key={sectionTitle}>
      <h3>{sectionTitle}</h3>
      {groups.map(({ name: category, images }) => {
        const preview = images.slice(0, PREVIEW_COUNT);
        const total = Math.min(images.length, SEE_ALL_MAX);
        return (
          <div key={`${sectionTitle}-${category}`} className="category-group">
            <div className="category-header">
              <p className="category-name">{category}</p>
              <button
                type="button"
                className="see-all-link"
                onClick={() => handleSeeAllClick(category, images)}
              >
                See all
              </button>
            </div>
            <div className="images-grid images-grid--preview-row">
              {preview.map((texture) => (
                <button
                  key={texture.id}
                  type="button"
                  className={`image-tile-wrap${
                    texture.orientation === "portrait"
                      ? " image-tile-wrap--portrait"
                      : texture.orientation === "landscape"
                        ? " image-tile-wrap--landscape"
                        : ""
                  }`}
                  disabled={applyBusy}
                  onClick={() => handleTextureClick(texture.id, texture)}
                >
                  <img
                    src={texture.image}
                    alt={texture.title || texture.id}
                    className="image-tile"
                    onError={(e) => {
                      e.target.style.backgroundColor = "#404040";
                    }}
                  />
                </button>
              ))}
            </div>
            {total > PREVIEW_COUNT ? (
              <p className="category-count-hint">{total} backgrounds</p>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  if (viewingCategory) {
    return (
      <BackgroundImageCatagoryMenu
        categoryName={categoryData.name}
        categoryImages={categoryData.images}
        onBack={handleBackToMain}
        onApplyImageBackground={(url, item) => {
          if (item) applyWithTracking(item);
          else onApplyImageBackground(url);
        }}
      />
    );
  }

  return (
    <div className="background-image-controls-menu">
      <ImagePickerModal {...imagePickerProps} />
      {showLocalUpload && (
        <div
          className="upload-area"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={openBackgroundPicker}
        >
          <div className="upload-content">
            <span className="upload-icon">☁️</span>
            <p className="upload-text">Upload or Drop your image here</p>
          </div>
        </div>
      )}

      {showStockSearch && (
        <div className="search-section">
          <div className="search-form">
            <input
              type="text"
              placeholder="Search Unsplash backgrounds"
              className="search-input"
              value={stockQuery}
              onChange={(e) => setStockQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleStockSearch();
              }}
              disabled={!onUnsplashSearch || applyBusy}
            />
            <button
              type="button"
              className="search-btn"
              disabled={stockSearching || !stockQuery.trim() || !onUnsplashSearch}
              onClick={handleStockSearch}
            >
              {stockSearching ? "…" : "Search"}
            </button>
            {showingSearchResults ? (
              <button type="button" className="see-all-link" onClick={clearSearch}>
                Clear
              </button>
            ) : null}
          </div>
          {!onUnsplashSearch ? (
            <p className="search-hint">Sign in to search Unsplash.</p>
          ) : (
            <p className="search-hint">
              Live search is not saved. Prefer empty product-safe scenes.
            </p>
          )}
          {stockSearchError ? <p className="search-error">{stockSearchError}</p> : null}
          {showingSearchResults ? (
            <div className="search-results-grid">
              {stockResults.map((item) => (
                <button
                  key={item.id || item.image_url}
                  type="button"
                  className="search-result-tile"
                  disabled={applyBusy}
                  onClick={() => handlePickStockImage(item)}
                >
                  <img src={item.thumbnail_url || item.image_url} alt="" />
                </button>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {!showingSearchResults && catalogLoading ? (
        <p className="empty-message" style={{ padding: 16 }}>
          Loading backgrounds…
        </p>
      ) : null}
      {!showingSearchResults && catalogError && !catalogLoading ? (
        <p style={{ fontSize: 13, color: "#f66", padding: 16 }}>{catalogError}</p>
      ) : null}
      {!showingSearchResults && !catalogLoading && !catalogError
        ? sectionList.map(({ sectionTitle, groups }) =>
            renderCategorySection(sectionTitle, groups)
          )
        : null}
      {!showingSearchResults &&
      !catalogLoading &&
      !catalogError &&
      sectionList.length === 0 ? (
        <p className="empty-message" style={{ padding: 16 }}>
          {IS_APP_V1
            ? "No curated backgrounds yet. Run the catalog sync to populate."
            : "No curated backgrounds yet. Run the catalog sync or search above."}
        </p>
      ) : null}
    </div>
  );
};

export default BackgroundImageControlsMenu;
